/**
 * The Learn library, as the app sees it.
 *
 * `lessons.json` is WRITTEN by `app/scripts/build-learn.ts` from `content/lessons/` and
 * committed — never edited here. The harness aliases it to `lessons.samples.json`, the
 * same build with the plumbing fixtures in (`harness/vite.config.ts`), so the Study shelf
 * can be looked at before the Study is written. A sample never reaches the app's bundle.
 *
 * What this module adds is the SHAPE of the shelves: which pages the outline promises
 * (`OUTLINE`, transcribed from `plan/content/LEARN-OUTLINE.md` by Phase 2b and moved here
 * from `rooms/Panels.tsx`), which of them are written, and Your Syllabus's order.
 */
import INDEX from "./lessons.json";
import type { LeakSegment } from "../lib/report-view";

/* The types are the build's (`scripts/learn-lib.ts`), restated: the web tsconfig does
   not reach `app/scripts/`, and a structural copy is checked by `tests/learn.test.ts`. */
export interface CardTarget { id: string; kind: "play" | "train" | "custom" | "surface"; title: string; href: string }
export interface RangeNode {
  path: string; street: string; board: string[]; actor: string; potBb: number;
  stacksBb: Record<string, number>;
  actions: { id: string; label: string; size?: number; sizeUnit?: string }[];
  strategy: Record<string, { freqs: number[]; weight?: number; indifferent?: boolean }>;
}
export type Block =
  | { t: "h2" | "h3"; text: string }
  | { t: "p"; html: string }
  | { t: "ul" | "ol"; items: string[] }
  | { t: "quote"; html: string }
  | { t: "range"; ref: string; chartKey: string; label: string; action: string; node: RangeNode }
  | { t: "try"; html: string };
export interface Checkpoint { questions: { q: string; options: { html: string; correct: boolean }[]; why: string | null }[] }
export interface Lesson {
  id: string; shelf: "primer" | "study"; track: string; trackLabel: string; title: string; promise: string;
  narrator: string; narratorName: string; card: CardTarget; leak: string;
  readingMinutes: number; free: boolean; sample: boolean; words: number;
  blocks?: Block[];
  checkpoint?: Checkpoint | null;
}

export const LESSONS: readonly Lesson[] = (INDEX as unknown as { lessons: Lesson[] }).lessons;
export const lessonById = (id: string): Lesson | undefined => LESSONS.find((l) => l.id === id);

/**
 * The outline — every page and lesson the library promises, in writing order, keyed by
 * the id prefix its file will carry (`primer-05-…`, `study-tournament-03-…`). A written
 * lesson takes its slot; an unwritten one is listed as being written, because a player
 * who is told what is coming can decide whether to care.
 */
export interface Planned { key: string; shelf: "primer" | "study"; track: string; title: string }
const P = (n: number, title: string): Planned => ({ key: `primer-${String(n).padStart(2, "0")}`, shelf: "primer", track: "primer", title });
const S = (track: string, n: number, title: string): Planned => ({ key: `study-${track}-${String(n).padStart(2, "0")}`, shelf: "study", track, title });
export const OUTLINE: readonly Planned[] = [
  P(1, "The table"), P(2, "How a hand goes"), P(3, "What you can do"), P(4, "Hand rankings"),
  P(5, "Position"), P(6, "Blinds, antes and stacks"), P(7, "Cash versus tournament"), P(8, "What a chart is"),
  P(9, "What “EV lost” means"), P(10, "Buster Bucks"), P(11, "The leak report"), P(12, "Fair, and how to check"),
  S("tournament", 1, "Chips are not money"), S("tournament", 2, "The bubble is not a wall"), S("tournament", 3, "Laddering"),
  S("tournament", 4, "Short-stack shoving"), S("tournament", 5, "Bounties change the price"),
  S("foundations", 1, "Equity"), S("foundations", 2, "Pot odds and the price of a call"), S("foundations", 3, "Ranges, not hands"),
  S("foundations", 4, "Fold equity"), S("foundations", 5, "The fundamental theorem"),
  S("preflop", 1, "Opening"), S("preflop", 2, "Three-betting"), S("preflop", 3, "Stack depth is everything"),
  S("preflop", 4, "Defending the blinds"), S("preflop", 5, "Reading the chart"),
  S("headsup", 1, "Two players, every hand"), S("headsup", 2, "Shoving and calling heads-up"), S("headsup", 3, "Winning the last hand"),
  S("reading", 1, "Nine kinds of player"), S("reading", 2, "Beating the loose"), S("reading", 3, "Beating the tight"),
  S("postflop", 1, "Continuation betting"), S("postflop", 2, "Board texture"),
  S("mindset", 1, "Variance, honestly"), S("mindset", 2, "Playing for practice"),
];
export const STUDY_TRACKS = ["tournament", "foundations", "preflop", "headsup", "reading", "postflop", "mindset"] as const;
export const TRACK_LABEL: Record<string, string> = {
  primer: "The Primer", tournament: "Tournament", foundations: "Foundations", preflop: "Preflop",
  headsup: "Heads-up", reading: "Reading the table", postflop: "Postflop — modelled", mindset: "Bankroll and mindset",
};

/** A shelf entry: a written lesson, or a planned title still being written. */
export type Entry = { lesson: Lesson; planned?: Planned } | { lesson?: undefined; planned: Planned };

/** A lesson's slot in the outline, by its id prefix. Samples carry `sample-` in front. */
const slotOf = (l: Lesson): string => l.id.replace(/^sample-/, "").split("-").slice(0, l.shelf === "primer" ? 2 : 3).join("-");

/** The shelf as a player sees it: the outline's order, written lessons in their slots. */
export function shelf(which: "primer" | "study", lessons: readonly Lesson[] = LESSONS): Entry[] {
  const on = lessons.filter((l) => l.shelf === which);
  const used = new Set<string>();
  const out: Entry[] = OUTLINE.filter((p) => p.shelf === which).map((p) => {
    const l = on.find((x) => slotOf(x) === p.key && !used.has(x.id));
    if (l) { used.add(l.id); return { lesson: l, planned: p }; }
    return { planned: p };
  });
  /* A written lesson the outline did not foresee is still on the shelf, at its track's end. */
  for (const l of on) {
    if (used.has(l.id)) continue;
    const at = out.map((e) => (e.lesson ?? e.planned)!.track).lastIndexOf(l.track);
    out.splice(at < 0 ? out.length : at + 1, 0, { lesson: l });
  }
  return out;
}

/**
 * YOUR SYLLABUS — the Study reordered by the player's own leak report (master plan §8).
 *
 * A lesson whose `leak` names a segment the report can diagnose (enough spots, a cost
 * measured) is ranked by that segment's avoidable loss, worst first; everything else
 * keeps the outline's order after them. Pro only: the report's segments are Pro
 * (`full_leak_report`), so a free account sees the outline's order and the lock. Nothing
 * is invented — a lesson is moved up only by a number the report actually printed.
 */
export interface SyllabusEntry { entry: Entry; because: LeakSegment | null }
export function syllabus(entries: readonly Entry[], segments: readonly LeakSegment[]): SyllabusEntry[] {
  const seg = (leak: string) => segments.find((s) => `${s.axis}:${s.key}` === leak
    && s.diagnosable && s.evLostPer100 !== null && s.evLostPer100 > 0) ?? null;
  const scored = entries.map((entry, i) => ({ entry, i, because: entry.lesson ? seg(entry.lesson.leak) : null }));
  return scored.sort((a, b) => {
    const x = a.because?.evLostPer100 ?? -1, y = b.because?.evLostPer100 ?? -1;
    return y - x || a.i - b.i;
  }).map(({ entry, because }) => ({ entry, because }));
}
